/**
 * Centralized, security-safe error handling.
 *
 * Goal: only use HTTP status codes to decide what to show to end users.
 * Never surface backend-provided messages in the UI.
 */

/**
 * @typedef {Object} ApiError
 * @property {number|null} status HTTP status code if known
 * @property {string} context A short context key (login, register, purchase, etc.)
 */

/**
 * Error type we throw from service layer.
 * The UI should map this using `getUserFacingErrorMessage`.
 */
export class ApiError extends Error {
  /**
   * @param {string} context
   * @param {number|null} status
   */
  constructor(context, status) {
    super('Request failed');
    this.name = 'ApiError';
    this.context = context;
    this.status = typeof status === 'number' ? status : null;
  }
}

/**
 * Convert a fetch Response into an ApiError (does not read body).
 * @param {Response} response
 * @param {string} context
 */
export function apiErrorFromResponse(response, context) {
  return new ApiError(context, response?.status);
}

/**
 * Best-effort extraction of status code from generic thrown values.
 * We explicitly ignore message/body content for UI display.
 * @param {unknown} error
 * @returns {{ status: number|null }}
 */
export function extractStatus(error) {
  if (!error) return { status: null };

  if (error instanceof ApiError) return { status: error.status };

  // If someone threw something like { status: 401 }
  if (typeof error === 'object' && 'status' in error) {
    const s = /** @type {any} */ (error).status;
    return { status: typeof s === 'number' ? s : null };
  }

  // Legacy: services used to throw Error("Error 400: ...")
  if (error instanceof Error) {
    const match = /\b(\d{3})\b/.exec(error.message);
    if (match) return { status: Number(match[1]) };
  }

  return { status: null };
}

const defaultMessages = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'You’re not authorized. Please sign in and try again.',
  404: 'Not found.',
  409: 'This action couldn’t be completed due to a conflict. Please try again.',
  500: 'Something went wrong on our end. Please try again later.',
  unknown: 'Something went wrong. Please try again.',
};

/**
 * Context-specific overrides. Keep these generic (no backend details).
 */
const contextOverrides = {
  login: {
    400: 'Invalid email or password.',
    401: 'Invalid email or password.',
    500: defaultMessages[500],
  },
  register: {
    400: 'Please double-check your details and try again.',
    409: 'An account with that email already exists. Try signing in instead.',
  },
  changePassword: {
    400: 'Please check your current password and try again.',
    401: 'Please sign in again to change your password.',
  },
  requestResetPassword: {
    // Backend always returns 200 with generic message; for errors, keep generic.
    400: defaultMessages[400],
  },
  resetPassword: {
    400: 'That reset link is invalid or expired. Please request a new one.',
  },
  purchaseInitiate: {
    400: 'We couldn’t start the purchase. Please review your selections and try again.',
    401: 'Please sign in to make a purchase.',
  },
  purchaseRedeem: {
    400: 'We couldn’t redeem that code. Please double-check it and try again.',
    401: 'Please sign in to redeem a code.',
    404: 'We couldn’t find that code. Please double-check and try again.',
  },
  surveySave: {
    400: 'Some survey answers look invalid. Please review and try again.',
    401: 'Please sign in to submit your survey.',
  },
  anonSurvey: {
    400: 'We couldn’t submit your survey. Please check your answers and try again.',
  },
  claim: {
    400: 'We couldn’t claim this result. Please verify the claim code and try again.',
    404: 'We couldn’t find that claim. Please double-check and try again.',
    409: 'This claim has already been used.',
  },
  access: {
    401: 'Please sign in to view your access.',
    403: 'You don’t have permission to view this resource.',
    500: defaultMessages[500],
  },
};

/**
 * Map an error to a safe, user-facing message.
 * @param {unknown} error
 * @param {string} context
 */
export function getUserFacingErrorMessage(error, context) {
  const { status } = extractStatus(error);
  const normalizedContext = context || 'unknown';

  const overrides = contextOverrides[normalizedContext];
  if (overrides && status && overrides[status]) return overrides[status];

  // allow context override for 403 too
  if (overrides && status === 403 && overrides[403]) return overrides[403];

  if (status && defaultMessages[status]) return defaultMessages[status];
  return defaultMessages.unknown;
}
